import * as React from "react"
import { AlertTriangle, Ban, Bookmark, Clock, FilePlus2, History } from "lucide-react"
import { Menu } from "@ui/Menu"
import { Tabs } from "@ui/Tabs"
import { cn } from "@lib/cn"
import type { RequestRun } from "@lib/inertia-types"
import { CodeEditor } from "./CodeEditor"

const STATUS_STYLES: Record<string, string> = {
  success: "text-cloverhound-green-600 dark:text-cloverhound-green-400",
  http_error: "text-cloverhound-orange-500 dark:text-cloverhound-orange-300",
  timeout: "text-cloverhound-yellow-600 dark:text-cloverhound-yellow-400",
  blocked: "text-cloverhound-red-500 dark:text-cloverhound-red-400",
  error: "text-cloverhound-red-500 dark:text-cloverhound-red-400",
}

function formatBytes(n: number | null): string {
  if (n === null) return ""
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

function headerValue(headers: Record<string, string> | null, name: string): string | null {
  if (!headers) return null
  const hit = Object.entries(headers).find(([k]) => k.toLowerCase() === name.toLowerCase())
  return hit ? hit[1] : null
}

// Reindent XML, but only after DOMParser confirms it is well-formed — otherwise
// return null so the caller shows the raw body untouched rather than mangling it.
function formatXml(src: string): string | null {
  try {
    const doc = new DOMParser().parseFromString(src, "application/xml")
    if (doc.getElementsByTagName("parsererror").length > 0) return null
  } catch {
    return null
  }
  let pad = 0
  return src
    .replace(/>\s*</g, ">\n<")
    .trim()
    .split("\n")
    .map((raw) => {
      const line = raw.trim()
      if (!line) return null
      const isClosing = /^<\//.test(line)
      const isDecl = /^<[?!]/.test(line)
      const isSelfClose = /\/>$/.test(line)
      const isOpenClose = /^<([\w:.-]+)(\s[^>]*)?>.*<\/\1>$/.test(line)
      if (isClosing) pad = Math.max(pad - 1, 0)
      const out = "  ".repeat(pad) + line
      if (/^<[^/?!]/.test(line) && !isSelfClose && !isOpenClose && !isDecl) pad += 1
      return out
    })
    .filter((l): l is string => l !== null)
    .join("\n")
}

// Pretty-print + choose a syntax for the response body, preferring the declared
// Content-Type and falling back to sniffing the first non-space character.
function prettyBody(
  body: string,
  contentType: string | null,
): { text: string; language: "json" | "xml" | "text" } {
  const ct = (contentType ?? "").toLowerCase()
  const head = body.trimStart()
  const looksJson = ct.includes("json") || head.startsWith("{") || head.startsWith("[")
  const looksXml = ct.includes("xml") || ct.includes("html") || head.startsWith("<")

  if (looksJson) {
    try {
      return { text: JSON.stringify(JSON.parse(body), null, 2), language: "json" }
    } catch {
      /* not valid JSON after all — fall through */
    }
  }
  if (looksXml) {
    return { text: formatXml(body) ?? body, language: "xml" }
  }
  return { text: body, language: "text" }
}

export function ResponsePane({
  run,
  running,
  history,
  onSelectHistory,
  onSaveAsMock,
  savingMock = false,
  onSaveAsExample,
  savingExample = false,
}: {
  run: RequestRun | null
  running: boolean
  history: RequestRun[]
  onSelectHistory?: (run: RequestRun) => void
  onSaveAsMock?: (run: RequestRun) => void
  savingMock?: boolean
  onSaveAsExample?: (run: RequestRun) => void
  savingExample?: boolean
}) {
  if (running && !run) {
    return (
      <div className="grid h-full place-items-center text-small text-cloverhound-gray-600">
        <div className="flex items-center gap-2">
          <Clock className="size-4 animate-pulse" aria-hidden="true" />
          Sending…
        </div>
      </div>
    )
  }

  if (!run) {
    return (
      <div className="grid h-full place-items-center px-6 text-center text-small text-cloverhound-gray-600">
        Send the request (⌘/Ctrl + Enter) to see the response here.
      </div>
    )
  }

  const body = run.response_body ?? ""
  const pretty = prettyBody(body, headerValue(run.response_headers, "content-type"))

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-3 border-b border-cloverhound-gray-200 px-4 py-2 dark:border-cloverhound-gray-850">
        <StatusSummary run={run} />
        <div className="ml-auto flex items-center gap-1">
          {onSaveAsExample && run.request_snapshot && (
            <button
              type="button"
              onClick={() => onSaveAsExample(run)}
              disabled={savingExample}
              className="flex items-center gap-1 rounded-md px-2 py-1 text-mini text-cloverhound-gray-700 hover:bg-cloverhound-gray-100 disabled:opacity-50 dark:text-cloverhound-gray-300 dark:hover:bg-cloverhound-gray-850"
              title="Save this run's params (query + body) as a reusable example"
            >
              <Bookmark className="size-3.5" aria-hidden="true" />
              {savingExample ? "Saving…" : "Save as example"}
            </button>
          )}
          {onSaveAsMock && run.response_status !== null && (
            <button
              type="button"
              onClick={() => onSaveAsMock(run)}
              disabled={savingMock}
              className="flex items-center gap-1 rounded-md px-2 py-1 text-mini text-cloverhound-gray-700 hover:bg-cloverhound-gray-100 disabled:opacity-50 dark:text-cloverhound-gray-300 dark:hover:bg-cloverhound-gray-850"
              title="Create a mock variant that replays this response (status, headers, body)"
            >
              <FilePlus2 className="size-3.5" aria-hidden="true" />
              {savingMock ? "Saving…" : "Save as mock"}
            </button>
          )}
          {history.length > 0 && onSelectHistory && (
            <Menu.Root>
              <Menu.Trigger className="flex items-center gap-1 rounded-md px-2 py-1 text-mini text-cloverhound-gray-700 hover:bg-cloverhound-gray-100 dark:text-cloverhound-gray-300 dark:hover:bg-cloverhound-gray-850">
                <History className="size-3.5" aria-hidden="true" />
                History
              </Menu.Trigger>
              <Menu.Content align="end" sideOffset={4} className="max-h-80 overflow-auto">
                {history.map((h) => (
                  <Menu.Item key={h.id} onClick={() => onSelectHistory(h)}>
                    <span className={cn("font-mono text-mini font-semibold", STATUS_STYLES[h.status])}>
                      {h.response_status ?? h.status}
                    </span>
                    <span className="text-mini text-cloverhound-gray-600">
                      {new Date(h.created_at).toLocaleTimeString()}
                    </span>
                    {h.duration_ms !== null && (
                      <span className="ml-auto text-mini text-cloverhound-gray-500">{h.duration_ms}ms</span>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Content>
            </Menu.Root>
          )}
        </div>
      </div>

      {run.warnings && run.warnings.length > 0 && (
        <div className="flex items-start gap-2 border-b border-cloverhound-yellow-500/30 bg-cloverhound-yellow-50 px-4 py-2 text-mini text-cloverhound-yellow-700 dark:bg-cloverhound-yellow-500/10 dark:text-cloverhound-yellow-400">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
          <div>{run.warnings.join(" · ")}</div>
        </div>
      )}

      {run.error_message && (
        <div className="flex items-start gap-2 border-b border-cloverhound-red-500/30 bg-cloverhound-red-50 px-4 py-2 text-mini text-cloverhound-red-600 dark:bg-cloverhound-red-500/10 dark:text-cloverhound-red-400">
          <Ban className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
          <div>{run.error_message}</div>
        </div>
      )}

      <Tabs.Root defaultValue="body" className="min-h-0 flex-1 gap-0">
        <Tabs.List className="px-4">
          <Tabs.Tab value="body">Body</Tabs.Tab>
          <Tabs.Tab value="headers">
            Headers
            {run.response_headers && (
              <span className="ml-1 text-micro text-cloverhound-gray-500">
                {Object.keys(run.response_headers).length}
              </span>
            )}
          </Tabs.Tab>
          <Tabs.Tab value="request">Request</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="body" className="min-h-0 flex-1 overflow-auto p-4">
          {body ? (
            <CodeEditor value={pretty.text} language={pretty.language} readOnly minHeight="100%" />
          ) : (
            <p className="text-small text-cloverhound-gray-600">No response body.</p>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="headers" className="min-h-0 flex-1 overflow-auto p-4">
          <HeaderTable headers={run.response_headers} />
        </Tabs.Panel>

        <Tabs.Panel value="request" className="min-h-0 flex-1 overflow-auto p-4">
          <RequestSnapshot snapshot={run.request_snapshot} />
        </Tabs.Panel>
      </Tabs.Root>
    </div>
  )
}

function StatusSummary({ run }: { run: RequestRun }) {
  return (
    <div className="flex items-center gap-3 text-small">
      <span className={cn("font-mono font-semibold", STATUS_STYLES[run.status])}>
        {run.response_status ?? run.status.toUpperCase()}
      </span>
      {run.duration_ms !== null && (
        <span className="flex items-center gap-1 text-cloverhound-gray-600">
          <Clock className="size-3.5" aria-hidden="true" />
          {run.duration_ms} ms
        </span>
      )}
      {run.response_body_bytes !== null && (
        <span className="text-cloverhound-gray-600">{formatBytes(run.response_body_bytes)}</span>
      )}
    </div>
  )
}

function HeaderTable({ headers }: { headers: Record<string, string> | null }) {
  if (!headers || Object.keys(headers).length === 0) {
    return <p className="text-small text-cloverhound-gray-600">No headers.</p>
  }
  return (
    <div className="flex flex-col gap-1 font-mono text-mini">
      {Object.entries(headers).map(([key, value]) => (
        <div key={key} className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-3 border-b border-cloverhound-gray-150 py-1 dark:border-cloverhound-gray-850">
          <span className="truncate font-semibold text-cloverhound-gray-700 dark:text-cloverhound-gray-300">{key}</span>
          <span className="break-all text-cloverhound-gray-900 dark:text-cloverhound-gray-100">{value}</span>
        </div>
      ))}
    </div>
  )
}

function RequestSnapshot({ snapshot }: { snapshot: RequestRun["request_snapshot"] }) {
  return (
    <div className="flex flex-col gap-3 font-mono text-mini">
      <div>
        <span className="font-semibold text-cloverhound-gray-700 dark:text-cloverhound-gray-300">
          {snapshot.method}
        </span>{" "}
        <span className="break-all text-cloverhound-gray-900 dark:text-cloverhound-gray-100">{snapshot.url}</span>
      </div>
      <HeaderTable headers={snapshot.headers} />
      {snapshot.body && (
        <div>
          <p className="mb-1 font-semibold text-cloverhound-gray-700 dark:text-cloverhound-gray-300">Body</p>
          <pre className="whitespace-pre-wrap break-all text-cloverhound-gray-900 dark:text-cloverhound-gray-100">
            {snapshot.body}
          </pre>
        </div>
      )}
      <p className="text-micro text-cloverhound-gray-500">
        Secrets and credentials are redacted in this snapshot.
      </p>
    </div>
  )
}
